import React from 'react'
import { useTheme } from 'particles/themes/styled'
import { Theme } from 'particles/themes'
import { TFunction } from 'i18next'
import { useNewTranslation } from 'hooks/index'
import Text, { TextColor } from 'particles/Text'
import { PopperContentContainer, DotContentContainer, StyledTimelineDot } from './styles'
import { ContentType } from 'state/review'
import { jsDateIsInPast, addDay } from 'services/dates'
import { COUNTRY_ENUM } from 'services/api/constants'

interface DotContentProps {
    dotColor: string
    firstTitle: string
    description: string
    firstTitleDate?: string
    descriptionStyle?: 'default' | 'bold'
    secondTitle?: string
    secondTitleColor?: TextColor
}

const DotContent: React.FC<DotContentProps> = ({
    dotColor,
    firstTitle,
    description,
    firstTitleDate,
    secondTitle,
    secondTitleColor = 'primary',
    descriptionStyle = 'default',
}) => {
    return (
        <DotContentContainer>
            <div className="dot">
                <StyledTimelineDot dotColor={dotColor} />
            </div>
            <div className="description-container">
                <div className="description-title">
                    <div className="first-title">
                        <Text color="primary" scale={'bodyS700'}>
                            {firstTitle}
                        </Text>
                        {firstTitleDate && (
                            <Text color="primary" scale={'bodyS700'}>
                                {firstTitleDate}
                            </Text>
                        )}
                    </div>

                    {secondTitle && (
                        <Text color={secondTitleColor} scale={'bodyS700'}>
                            {secondTitle}
                        </Text>
                    )}
                </div>
                <Text
                    color={descriptionStyle === 'bold' ? 'primary' : 'secondary'}
                    scale={descriptionStyle === 'bold' ? 'bodyS700' : 'bodyS500'}
                >
                    {description}
                </Text>
            </div>
        </DotContentContainer>
    )
}

const getDayLeftDisplay = (t: TFunction, country: COUNTRY_ENUM, endDate: string, dayLeft: number) => {
    if (dayLeft <= 0 && jsDateIsInPast(addDay(endDate, 1), country)) {
        return t('statusLabel.ended')
    } else if (dayLeft == 0 && !jsDateIsInPast(addDay(endDate, 1), country)) {
        return t('statusLabel.lessThanOneDay')
    } else if (dayLeft > 0 && dayLeft < 7) {
        return t('statusLabel.lessThanOneWeek')
    } else {
        return t('statusLabel.numberOfDaysLeft', { count: dayLeft })
    }
}

const getDayLeftColor = (country: COUNTRY_ENUM, endDate: string, dayLeft: number) => {
    if (dayLeft == 0 && !jsDateIsInPast(addDay(endDate, 1), country)) {
        return 'error'
    } else if (dayLeft > 0 && dayLeft < 7) {
        return 'warning'
    } else {
        return 'primary'
    }
}

interface CampaignDate {
    startDate?: string
    endDate?: string
}

interface ReviewDate {
    startDate?: string
    endDate?: string
    dayLeft?: number
}

interface RequestEditDate {
    endDate?: string
    dayLeft?: number
}

interface GetDotContentProps {
    (
        t: TFunction,
        theme: Theme,
        country: COUNTRY_ENUM,
        contentType?: ContentType,
        reviewDate?: ReviewDate,
        requestEditDate?: RequestEditDate,
    ): DotContentProps[]
}

const getDotsContent: GetDotContentProps = (
    t: TFunction,
    theme: Theme,
    country: COUNTRY_ENUM,
    contentType?: ContentType,
    reviewDate?: ReviewDate,
    requestEditDate?: RequestEditDate,
): DotContentProps[] => {
    const baseDotContent = [
        {
            dotColor: theme.colors.surface.positive,
            firstTitle: t('submission.review.timelineHeader.reviewStartDateTitleTooltip'),
            firstTitleDate: reviewDate?.startDate,
            description: t('submission.review.timelineHeader.reviewStartDateSubtitleTooltip'),
        },
    ]
    if (contentType === 'registration') {
        return [
            {
                dotColor: theme.colors.surface.positive,
                firstTitle: t('submission.review.timelineHeader.registrationStartDateTitleTooltip'),
                firstTitleDate: reviewDate?.startDate,
                description: t('submission.review.timelineHeader.registrationStartDateSubtitleTooltip'),
            },
            {
                dotColor: theme.colors.surface.error,
                firstTitle: t('submission.review.timelineHeader.approvalRegistrationEndDateTitleTooltip'),
                firstTitleDate: reviewDate?.endDate,
                description: t('submission.review.timelineHeader.approvalRegistrationEndDateSubtitleTooltip'),
                secondTitle:
                    typeof reviewDate?.dayLeft === 'number' && reviewDate?.endDate
                        ? getDayLeftDisplay(t, country, reviewDate?.endDate, reviewDate?.dayLeft)
                        : undefined,
                secondTitleColor:
                    typeof reviewDate?.dayLeft === 'number' && reviewDate?.endDate
                        ? getDayLeftColor(country, reviewDate?.endDate, reviewDate?.dayLeft)
                        : 'primary',
            },
        ]
    } else if (contentType === 'allSubmission') {
        return [
            ...baseDotContent,
            {
                dotColor: theme.colors.surface.error,
                firstTitle: t('submission.review.timelineHeader.approvalEndDateTitleTooltip'),
                description: t('submission.review.timelineHeader.noEndDateApprovalSubtitleTooltip'),
                descriptionStyle: 'bold',
            },
        ]
    } else if (contentType === 'submissionNotInLastStep') {
        return [
            ...baseDotContent,
            {
                dotColor: theme.colors.surface.warning,
                firstTitle: t('submission.review.timelineHeader.requestEditTitleTooltip'),
                firstTitleDate: requestEditDate?.endDate,
                description: t('submission.review.timelineHeader.requestEditSubtitleTooltip'),
                secondTitle:
                    typeof requestEditDate?.dayLeft === 'number' && requestEditDate?.endDate
                        ? getDayLeftDisplay(t, country, requestEditDate?.endDate, requestEditDate?.dayLeft)
                        : undefined,
                secondTitleColor:
                    typeof requestEditDate?.dayLeft === 'number' && requestEditDate?.endDate
                        ? getDayLeftColor(country, requestEditDate?.endDate, requestEditDate?.dayLeft)
                        : 'primary',
            },
            {
                dotColor: theme.colors.surface.error,
                firstTitle: t('submission.review.timelineHeader.approvalEndDateTitleTooltip'),
                firstTitleDate: reviewDate?.endDate,
                description: t('submission.review.timelineHeader.approvalEndDateSubtitleTooltip'),
                secondTitle:
                    typeof reviewDate?.dayLeft === 'number' && reviewDate?.endDate
                        ? getDayLeftDisplay(t, country, reviewDate?.endDate, reviewDate?.dayLeft)
                        : undefined,
                secondTitleColor:
                    typeof reviewDate?.dayLeft === 'number' && reviewDate?.endDate
                        ? getDayLeftColor(country, reviewDate?.endDate, reviewDate?.dayLeft)
                        : 'primary',
            },
        ]
    } else if (contentType === 'submissionInLastStep') {
        return [
            ...baseDotContent,
            {
                dotColor: theme.colors.surface.warning,
                firstTitle: t('submission.review.timelineHeader.requestEditTitleTooltip'),
                firstTitleDate: requestEditDate?.endDate,
                description: t('submission.review.timelineHeader.requestEditSubtitleTooltip'),
                secondTitle:
                    typeof requestEditDate?.dayLeft === 'number' && requestEditDate?.endDate
                        ? getDayLeftDisplay(t, country, requestEditDate?.endDate, requestEditDate?.dayLeft)
                        : undefined,
                secondTitleColor:
                    typeof requestEditDate?.dayLeft === 'number' && requestEditDate?.endDate
                        ? getDayLeftColor(country, requestEditDate?.endDate, requestEditDate?.dayLeft)
                        : 'primary',
            },
            {
                dotColor: theme.colors.surface.error,
                firstTitle: t('submission.review.timelineHeader.approvalEndDateTitleTooltip'),
                description: t('submission.review.timelineHeader.noEndDateApprovalSubtitleTooltip'),
                descriptionStyle: 'bold',
            },
        ]
    } else {
        return []
    }
}

export interface PopperContentProps {
    country: COUNTRY_ENUM
    contentType?: ContentType
    campaignDate?: CampaignDate
    reviewDate?: ReviewDate
    requestEditDate?: RequestEditDate
}

const PopperContent: React.FC<PopperContentProps> = ({
    country,
    contentType,
    campaignDate,
    reviewDate,
    requestEditDate,
}) => {
    const [t] = useNewTranslation()
    const theme = useTheme()
    const dotsContent = getDotsContent(t, theme, country, contentType, reviewDate, requestEditDate)
    return (
        <PopperContentContainer>
            <div>
                <Text color="primary" scale={'bodyS700'}>
                    {contentType === 'registration'
                        ? t('submission.review.timelineHeader.partipostersRegistrationDeadlineTitleTooltip')
                        : t('submission.review.timelineHeader.partipostersDeadlineTitleTooltip')}
                </Text>
                <Text color="primary" scale={'bodyS700'}>
                    {campaignDate?.startDate && campaignDate.endDate
                        ? campaignDate?.startDate + ' - ' + campaignDate.endDate
                        : ''}
                </Text>
            </div>
            <div className="content-divider" />
            <div className="dot-content-container">
                <Text color="primary" scale={'bodyS700'}>
                    {t('submission.review.timelineHeader.cmUsersDeadlineTitleTooltip')}
                </Text>
                <div className="dots">
                    {dotsContent.map((content, index) => {
                        return (
                            <DotContent
                                key={'dotContent' + index}
                                dotColor={content.dotColor}
                                firstTitle={content.firstTitle}
                                firstTitleDate={content.firstTitleDate}
                                description={content.description}
                                descriptionStyle={content.descriptionStyle}
                                secondTitle={content.secondTitle}
                                secondTitleColor={content.secondTitleColor}
                            />
                        )
                    })}
                </div>
            </div>
        </PopperContentContainer>
    )
}

export default PopperContent
